<script setup lang="ts">
import { InformationCircleIcon } from '@heroicons/vue/24/outline'
import { LButton, LIcon, LText } from '@repo/lui'
import { LSpinnerIcon } from '@repo/lui/icons'
import type { Slot } from 'vue'

withDefaults(
  defineProps<{
    loading?: boolean
    error?: string | Error | null
    fallback?: string
    fullPage?: boolean
    variant?: 'spinner' | 'skeleton'
    message?: string
  }>(),
  {
    loading: false,
    error: null,
    fallback: 'No content to display',
    fullPage: false,
    variant: 'skeleton',
  },
)

defineSlots<{
  default?: ReturnType<Slot>
  fallback?: ReturnType<Slot>
  error?: ReturnType<Slot>
  loading?: ReturnType<Slot>
}>()

const emit = defineEmits<{
  (e: 'try-again'): void
}>()

const getErrorMessage = (error: string | Error | null | undefined) => {
  if (!error) {
    return 'Something went wrong'
  }

  return error instanceof Error ? error.message : error
}
</script>

<template>
  <div
    :class="[fullPage && (loading || error || !$slots.default) ? 'fixed top-0 left-0 z-100 h-full w-full' : 'relative']"
  >
    <div
      v-if="loading || error || !$slots.default"
      class="h-full w-full"
      :class="[!(error || !$slots.default) ? 'absolute top-0 right-0 bottom-0 left-0' : '']"
    >
      <slot v-if="loading" name="loading">
        <div
          class="bg-lyka-cream-200 flex h-full flex-col items-center justify-center text-center"
          role="status"
          aria-live="polite"
        >
          <template v-if="variant === 'spinner'">
            <LIcon :is="LSpinnerIcon" size="xl" />
          </template>
          <div v-else class="bg-border-inactive h-full w-full animate-pulse rounded-xl"></div>
          <LText v-if="message" variant="body-lg" class="text-lyka-dark-green-500 mt-3 text-balance wrap-anywhere">
            {{ message }}
          </LText>
        </div>
      </slot>

      <div
        v-else-if="error"
        class="bg-lyka-cream-200 flex h-full flex-col items-center justify-center gap-4 rounded-2xl py-4 text-center"
      >
        <slot name="error">
          <div class="flex items-center gap-2">
            <LIcon :is="InformationCircleIcon" size="sm" />
            <LText variant="body-md">{{ getErrorMessage(error) }}</LText>
          </div>
        </slot>
        <LButton size="sm" @click="emit('try-again')">Try again</LButton>
      </div>

      <div
        v-else-if="!$slots.default"
        class="bg-lyka-cream-200 text-body-md flex h-full items-center justify-center rounded-2xl text-center"
      >
        <slot name="fallback">
          <LText variant="body-md">{{ fallback }}</LText>
        </slot>
      </div>
    </div>

    <div>
      <slot v-if="!loading && !error" />
    </div>
  </div>
</template>
